import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { AnalyticsCard } from '@/components/cards/AnalyticsCard'
import { calculateVariant } from '@/views/RealtimeCoaching/Reports/ReportInfoCards'
import { compareToPreviousRange } from '@/utils/helpers'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import NoData from '@/components/NoData'

const columns = [
  {
    accessor: 'name',
    label: 'Organization Name',
    noWrap: true,
  },
  {
    accessor: 'organization_id',
    label: 'ID',
  },
]

export const UsageCardsWidget = () => {
  const { data, loading } = useSelector((state) => state.analytics)
  const [showDetails, setShowDetails] = useState()
  const [detailsData, setDetailsData] = useState([])
  const { hierarchy_manager: isHierarchyManager, own_organization_id } = useSelector(
    (state) => state.currentUser
  )
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)

  const isBaltoAdmin = own_organization_id === 1
  // Balto admins view multi-org usage data without a hierarchy of their own, so they keep the
  // org cards; customer hierarchy managers only get them when their org actually has children
  const showOrganizationCards =
    isHierarchyManager && (isBaltoAdmin || flattenedUserOrgHierarchy.length > 1)

  const getTotalCalls = (callCounts) => {
    if (isEmpty(callCounts)) {
      return 0
    }
    return callCounts
      .map((usageReport) => {
        return usageReport.call_count
      })
      .reduce((a, b) => {
        if (b) return a + b
        return a
      })
  }
  const getUsersCallsCount = (callCounts, isWith) => {
    if (isEmpty(callCounts)) {
      return 0
    }
    return callCounts.filter((usageReport) => {
      if (isWith) return usageReport.call_count > 0
      return usageReport.call_count === 0
    }).length
  }
  const getOrganizations = (callCounts) => {
    const orgSeen = {}
    const result = []

    for (const call of callCounts) {
      const orgId = call.organization_id
      if (!orgSeen[orgId]) {
        orgSeen[orgId] = true
        result.push({
          organization_id: orgId,
          name: call.organization_name,
        })
      }
    }
    return result
  }
  const getDormantOrganizations = (callCounts) => {
    return callCounts
      .reduce((acc, current) => {
        const existingOrg = acc.find((item) => item.organization_id === current.organization_id)

        if (existingOrg) {
          existingOrg.call_count += current.call_count
        } else {
          acc.push({
            organization_id: current.organization_id,
            call_count: current.call_count,
            name: current.organization_name,
          })
        }

        return acc
      }, [])
      .filter((orgGroupedData) => orgGroupedData.call_count === 0)
  }
  const getTotalContractedSeats = (contractedSeatsData) => {
    if (isEmpty(contractedSeatsData) || !contractedSeatsData.contracted_seats) {
      return 0
    }
    return contractedSeatsData.contracted_seats.reduce((total, org) => {
      return total + Number(org.Contracted_RTG_Seats__c)
    }, 0)
  }

  const totalContractedSeats = useMemo(() => {
    return getTotalContractedSeats(data.contractedSeats)
  }, [data.contractedSeats])
  const totalCalls = useMemo(() => {
    return getTotalCalls(data.callCountsByUser)
  }, [data.callCountsByUser])
  const totalCallsPrevPeriod = useMemo(() => {
    return getTotalCalls(data.callCountsByUserPreviousPeriod)
  }, [data.callCountsByUserPreviousPeriod])

  const usersWithCalls = useMemo(() => {
    return getUsersCallsCount(data.callCountsByUser, true)
  }, [data.callCountsByUser])

  const usersWithCallsPrevPeriod = useMemo(() => {
    return getUsersCallsCount(data.callCountsByUserPreviousPeriod, true)
  }, [data.callCountsByUserPreviousPeriod])

  const usersWithoutCalls = useMemo(() => {
    return getUsersCallsCount(data.callCountsByUser, false)
  }, [data.callCountsByUser])

  const usersWithoutCallsPrevPeriod = useMemo(() => {
    return getUsersCallsCount(data.callCountsByUserPreviousPeriod, false)
  }, [data.callCountsByUserPreviousPeriod])
  const formatNumber = (number) => {
    return number?.toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  }
  const organizations = useMemo(() => {
    return getOrganizations(data.callCountsByUser)
  }, [data.callCountsByUser])
  const dormantOrganizations = useMemo(() => {
    return getDormantOrganizations(data.callCountsByUser)
  }, [data.callCountsByUser])
  const prevDormantOrganizations = useMemo(() => {
    return getDormantOrganizations(data.callCountsByUserPreviousPeriod)
  }, [data.callCountsByUserPreviousPeriod])

  return (
    <div className="analytics-card-grid">
      <AnalyticsCard
        loading={loading.usagePage}
        comparisonsLoading={loading.usagePage}
        arrowUp={usersWithCalls > usersWithCallsPrevPeriod}
        data={formatNumber(usersWithCalls)}
        testId="total-calls-card"
        dateDescription="vs. previous period"
        description="Agents taking calls "
        percent={formatNumber(compareToPreviousRange(usersWithCalls, usersWithCallsPrevPeriod))}
        variant={calculateVariant(
          compareToPreviousRange(usersWithCalls, usersWithCallsPrevPeriod),
          usersWithCalls > usersWithCallsPrevPeriod
        )}
      />

      <AnalyticsCard
        loading={loading.usagePage}
        comparisonsLoading={loading.usagePage}
        arrowUp={usersWithoutCalls > usersWithoutCallsPrevPeriod}
        data={formatNumber(usersWithoutCalls)}
        testId="total-calls-card"
        dateDescription="vs. previous period"
        description="Agents without calls"
        percent={formatNumber(
          compareToPreviousRange(usersWithoutCalls, usersWithoutCallsPrevPeriod)
        )}
        variant={calculateVariant(
          compareToPreviousRange(usersWithoutCalls, usersWithoutCallsPrevPeriod),
          usersWithoutCalls > usersWithoutCallsPrevPeriod
        )}
      />

      <AnalyticsCard
        loading={loading.contractedSeats}
        data={formatNumber(totalContractedSeats)}
        testId="contracted-seats-card"
        description="Contracted Seats"
      />

      <AnalyticsCard
        loading={loading.usagePage}
        comparisonsLoading={loading.usagePage}
        arrowUp={totalCalls > totalCallsPrevPeriod}
        data={formatNumber(totalCalls)}
        testId="total-calls-card"
        dateDescription="vs. previous period"
        description="Total Calls "
        percent={formatNumber(compareToPreviousRange(totalCalls, totalCallsPrevPeriod))}
        variant={calculateVariant(
          compareToPreviousRange(totalCalls, totalCallsPrevPeriod),
          totalCalls > totalCallsPrevPeriod
        )}
      />

      {showOrganizationCards && (
        <>
          <AnalyticsCard
            loading={loading.usagePage}
            data={formatNumber(organizations.length)}
            testId="organizations-card"
            dateDescription={
              <span
                className="link active"
                onClick={() => {
                  setDetailsData(organizations)
                  setShowDetails('ORG')
                }}
                data-testid="view-org-details"
                style={{
                  cursor: 'pointer',
                  color: 'var(--color-brand)',
                  textDecoration: 'underline',
                }}
              >
                View Details
              </span>
            }
            description="Organizations"
          />
          <AnalyticsCard
            loading={loading.usagePage}
            arrowUp={dormantOrganizations.length > prevDormantOrganizations.length}
            data={formatNumber(dormantOrganizations.length)}
            testId="dormant-organizations-card"
            dateDescription={
              <span
                className="link active"
                onClick={() => {
                  setDetailsData(dormantOrganizations)
                  setShowDetails('DOR')
                }}
                data-testid="view-dormant-details"
                style={{
                  cursor: 'pointer',
                  color: 'var(--color-brand)',
                  textDecoration: 'underline',
                }}
              >
                View Details
              </span>
            }
            description="Dormant Organizations"
          />
        </>
      )}

      {showDetails && (
        <BasicModal
          title={showDetails === 'DOR' ? 'Dormant Organizations' : 'Organizations'}
          onClose={() => setShowDetails(undefined)}
          show={!isEmpty(showDetails)}
          size="tiny"
          style={{ maxHeight: '600px', overflowY: 'auto' }}
        >
          {isEmpty(detailsData) ? (
            <div className="empty-table">
              <NoData />
            </div>
          ) : (
            <AdvancedTable columns={columns} rows={detailsData} />
          )}
        </BasicModal>
      )}
    </div>
  )
}
